import messages from "messages";
import {
  useCallback,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "react-bootstrap";
// styles
import styles from "./AvailabilityPane.module.scss";
// components
import PaneLayout from "../pane-layout";
import ScheduleCreatorContainer from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/components/schedule-creator-container/ScheduleCreatorContainer";
import Schedule from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/components/available-time-periods-viewer/components/schedule/Schedule";
// hooks
import useToggleModal from "hooks/useToggleModal";
import { useSchedule } from "components/Dashboard/DoctorDashboard/contexts/ScheduleContext";
import { useUser } from "contexts/UserContext";
// types
import type { PaneProps, PaneRefProps } from "../../MainSetup";
// services
import postSchedule from "services/scheduler/postSchedule";

type AvailabilityPaneProps = PaneProps & { onChange: (arg: Schedule) => void };

const ForwardedAvailabilityPane = forwardRef<
  PaneRefProps,
  AvailabilityPaneProps
>(function AvailabilityPane({ onShowModal, onChange, ...rest }, ref) {
  const { defaultUserTimezone } = useUser();
  const { schedule, setSchedule } = useSchedule();
  const { show, open, close } = useToggleModal(false);

  useEffect(() => onShowModal(show), [show, onShowModal]);

  const [localSchedule, setLocalSchedule] = useState(schedule);

  // track value
  useEffect(() => setLocalSchedule(schedule), [schedule]);

  // track ref pane data change
  useEffect(() => onChange(localSchedule), [localSchedule, onChange]);

  const [activeIndex, setActiveIndex] = useState(-1);

  const handleDelete = useCallback(
    (i: number) => {
      if (i === null) {
        return;
      }

      if (activeIndex === i) {
        setActiveIndex(-1);
      }

      setLocalSchedule((s) => [...s.slice(0, i), ...s.slice(i + 1)]);
    },
    [activeIndex],
  );

  const handleApply = async () => {
    // call api
    const res = await postSchedule(localSchedule);
    if (setSchedule) {
      setSchedule(res);
    }
  };

  useImperativeHandle(ref, () => ({
    handleApply,
  }));

  return (
    <>
      <PaneLayout
        Title={messages.setUpYourFirstCalendarAvailability}
        Subtitle={messages.setUpYourWeeklyAvailableHours}
        {...rest}
      >
        {localSchedule.length === 0 ? (
          <div className={styles["availability-pane__create-schedule"]}>
            <div className={styles["availability-pane__create-schedule-body"]}>
              <Button
                onClick={open}
                variant={"link"}
                className={styles["availability-pane__create-schedule-btn"]}
              >
                {messages["+createSchedule"]}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles["availability-pane__schedules"]}>
            <div className={styles["availability-pane__list-title"]}>
              {messages.mySchedule}
            </div>
            <div className={styles["availability-pane__list-container"]}>
              <div className={styles["availability-pane__list"]}>
                {localSchedule.map((el, i) => (
                  <Schedule
                    className={styles["availability-pane__list-item"]}
                    active={i === activeIndex}
                    key={i}
                    order={i + 1}
                    timezone={defaultUserTimezone}
                    value={el}
                    invalid={false}
                    onClick={() => setActiveIndex(i)}
                    onCopy={() => {
                      setLocalSchedule((s) => [
                        ...s.slice(0, i),
                        { ...s[i] },
                        ...s.slice(i),
                      ]);
                    }}
                    onDelete={() => handleDelete(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </PaneLayout>
      <ScheduleCreatorContainer
        show={show}
        onHide={close}
        backdrop={false}
        onChange={(v) => setLocalSchedule((s) => [...s, v])}
      />
    </>
  );
});

export default ForwardedAvailabilityPane;
